import React, { useEffect, useState } from 'react';
import { Link, Icon, Text, Gutter, Gap } from '@common/components';
import { useMutation } from '@common/hooks';
import {
  AUTHENTICATE_WORKS_USER_PASSWORD_MUTATION,
  SIGN_IN_WORKS_USER_MUTATION,
} from '@identity/graphql';
import { Form, Input, Button, Row, Col } from 'antd';
import Auth from 'works/legacy/lib/auth';
import { Colors, FontSize } from '@common/tokens';
import { defaultLoginRedirectPath } from 'works/legacy/lib/default-login-redirect-path';
import { useRedirectPath } from '@identity/hooks/use-redirect-path';
import { randomAccountColor } from '@identity/services';
import {
  SignInMembershipPicker,
  PreAuthMembership,
} from '@identity/components';
import moment from 'moment';
import { useQueryParam } from 'works/legacy/services/query-params-service';
import { REDIRECT_PATH_LABEL_KEY } from 'works/legacy/lib/redirect-to-login';

type Props = {
  email: string;
  onChangeEmail?: () => void;
  onBackAction?: (action: (() => void) | null) => void;
};

type AuthPhase =
  | { step: 'password' }
  | {
      step: 'membership-picker';
      preAuthToken: string;
      memberships: PreAuthMembership[];
      internalTrustedUser: boolean;
    };

export function SignInPasswordForm({
  email,
  onChangeEmail,
  onBackAction,
}: Props) {
  const [form] = Form.useForm();
  const [phase, setPhase] = useState<AuthPhase>({ step: 'password' });
  const [authenticatePassword, { loading: authenticating }] = useMutation(
    AUTHENTICATE_WORKS_USER_PASSWORD_MUTATION
  );
  const [signInWorksUser, { loading: signingIn }] = useMutation(
    SIGN_IN_WORKS_USER_MUTATION
  );
  const redirectPath = useRedirectPath();
  const redirectPathLabelEncoded = useQueryParam(REDIRECT_PATH_LABEL_KEY) || '';
  const redirectPathLabel = decodeURIComponent(redirectPathLabelEncoded);

  useEffect(() => {
    if (!onBackAction) return;

    if (phase.step === 'membership-picker') {
      onBackAction(() => setPhase({ step: 'password' }));
    } else {
      onBackAction(null);
    }
  }, [phase.step, onBackAction]);

  const completeSignIn = (preAuthToken: string, membershipId: string) => {
    signInWorksUser({
      variables: {
        input: { preAuthToken, worksMembershipId: membershipId },
      },
    }).then((result) => {
      if (result.errors) {
        form.setFields([
          {
            name: 'password',
            errors: ['Something went wrong. Please try again.'],
          },
        ]);
        setPhase({ step: 'password' });
        return;
      }

      const {
        identityToken,
        worksMembership: {
          id: resultMembershipId,
          name,
          entityType,
          saveLastVisitedPage,
          entity: { name: entityName },
        },
      } = result.data.signInWorksUser;

      const userState = {
        identityToken,
        email,
        entityType,
        membershipId: resultMembershipId,
        name,
        entityName,
        saveLastVisitedPage,
        path: redirectPath || defaultLoginRedirectPath(entityType),
        pathLabel: redirectPathLabel,
        pathUpdatedAt: moment().toISOString(),
        color: randomAccountColor({ exclude: Auth.colorsBeingUsed() }),
      };

      Auth.store(userState);

      window.location.href = userState.path;
    });
  };

  const onPasswordSubmit = (values: { password: string }) => {
    authenticatePassword({
      variables: { input: { email, password: values.password } },
    }).then((result) => {
      if (result.errors) {
        form.setFields([
          {
            name: 'password',
            errors: [
              'We couldn\u2019t sign you in. Double-check your email and password and try again.',
            ],
          },
        ]);
        return;
      }

      const { preAuthToken, memberships, internalTrustedUser } =
        result.data.authenticateWorksUserPassword;

      if (memberships.length === 1) {
        completeSignIn(preAuthToken, memberships[0].id);
      } else if (memberships.length > 1) {
        setPhase({
          step: 'membership-picker',
          preAuthToken,
          memberships,
          internalTrustedUser,
        });
      } else {
        form.setFields([
          {
            name: 'password',
            errors: [
              'We couldn\u2019t sign you in. Double-check your email and password and try again.',
            ],
          },
        ]);
      }
    });
  };

  if (phase.step === 'membership-picker') {
    return (
      <SignInMembershipPicker
        memberships={phase.memberships}
        internalTrustedUser={phase.internalTrustedUser}
        loading={signingIn}
        onSelect={(membershipId: string) =>
          completeSignIn(phase.preAuthToken, membershipId)
        }
      />
    );
  }

  return (
    <div className="sign-in-password-form">
      <Form form={form} onFinish={onPasswordSubmit}>
        {onChangeEmail && (
          <Gutter size="lg" direction="bottom">
            <Text weight="bold">Signing in as</Text>
            <Gap size="md" align="center">
              <Text>{email}</Text>
              <Button
                type="link"
                onClick={onChangeEmail}
                className="change-email-button"
              >
                Change
              </Button>
            </Gap>
          </Gutter>
        )}
        <Form.Item
          name="password"
          rules={[{ required: true, message: 'Please input your password!' }]}
        >
          <Input
            prefix={<Icon icon="lock" kind="light" />}
            type="password"
            placeholder="password"
            size="large"
            autoComplete="off"
          />
        </Form.Item>
        <Row align="middle">
          <Col span={12}>
            <Form.Item className="button-row">
              <Button
                type="primary"
                htmlType="submit"
                loading={authenticating || signingIn}
                className="sign-in-form-button"
                size="large"
              >
                Sign In
              </Button>
            </Form.Item>
          </Col>
          <Col span={12} className="text-right">
            <Link to="/works/auth/forgot-password">Forgot Password</Link>
          </Col>
        </Row>
      </Form>
      <style jsx>
        {`
          .sign-in-password-form {
            width: 100%;
          }

          .sign-in-password-form :global(.sign-in-form-button) {
            margin-bottom: 0px;
            width: auto;
          }

          .sign-in-password-form :global(.ant-input-prefix) {
            color: ${Colors.steelDarker};
          }
          .sign-in-password-form :global(input) {
            padding-left: 0.4rem;
          }

          .sign-in-password-form :global(.button-row) {
            margin-bottom: 0;
          }

          .sign-in-password-form :global(.change-email-button) {
            padding: 0;
            height: auto;
            font-size: ${FontSize.sm};
          }
        `}
      </style>
    </div>
  );
}
